/**
 * Знакомство с главной — при первом входе (03.11.2026).
 *
 * Владелец: «после входа впервые нужны всплывающие подсказки, что где
 * находится». Тот же вид, что у подсказки свайпа (`swipe-coach.tsx`): экран
 * затемнён, текст белым, кнопка салатовая. Отличие одно — подсвечено место,
 * о котором речь: «Можно тратить», «Записать», план, разделы, профиль.
 *
 * Шаги, которых на экране нет (у нового человека нет «Проверь сегодня»),
 * пропускаются. Пока знакомство открыто, нажать то, что под ним, нельзя:
 * человек, целивший в «Дальше», не должен попасть в «Записать».
 *
 * Меняется главная — меняются и шаги здесь (CLAUDE.md, «Подсказки-обучение»).
 */
import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { dismissHint } from './store.js';

/** Ключи «уже видел» в `dismissedHints`. */
export const HOME_TOUR_HINT = 'home-tour';
/** Окно записи при первом открытии (04.11.2026): как заполнять операцию. */
export const RECORD_TOUR_HINT = 'record-tour';

/** `target` — одно место или несколько: подсвечивается то, что их охватывает. */
interface Step { target: string | string[]; title: string; text: string }

const STEPS: Step[] = [
  { target: '.gh', title: 'Сколько можно тратить',
    text: 'Деньги до ближайшего прихода — платежи и отложенное уже вычтены. Стрелка справа покажет, как посчитано.' },
  { target: '.acts', title: 'Записать трату или приход',
    text: 'Вручную, голосом или фото чека. «Могу купить?» — проверить покупку заранее.' },
  /* Владелец 04.11.2026: «ты забыл показать, что записать можно плюсиком в правом углу». */
  { target: '.fab', title: 'Плюс — с любого экрана',
    text: 'Тот же «Записать»: кнопка в правом углу есть на каждом экране.' },
  { target: '.grail', title: 'Проверь сегодня',
    text: 'Платежи, по которым нужен ответ: оплачено или нет. Пока ответа нет, деньги на них отложены.' },
  { target: '.planrows', title: 'План на месяц',
    text: 'Сколько ушло на повседневные нужды и свободные траты и сколько осталось. Значок календаря — с какого дня считать.' },
  /* Где что добавлять — по вкладкам (04.11.2026): владелец не нашёл в знакомстве счета, платежи и цели. */
  { target: 'nav.tabs > :nth-child(2)', title: 'Деньги: счета, платежи, подписки',
    text: 'Счёт или кредит — «+» в «Деньгах». Платёж или подписка — «Деньги → Доход и платежи», «+» у нужного блока.' },
  { target: ['nav.tabs > :nth-child(3)', 'nav.tabs > :nth-child(4)'], title: 'Операции и аналитика',
    text: 'Операции — все записи, свайп влево по строке: изменить или удалить. Аналитика — куда уходят деньги за период.' },
  { target: 'nav.tabs > :nth-child(5)', title: 'Цели',
    text: 'На что копишь: подушка, отпуск, закрыть кредит. Новая цель — «+» в «Целях».' },
  { target: '.hcaps', title: 'Профиль и настройки',
    text: 'Кружок — профиль и подписка, колокольчик — уведомления, шестерёнка — настройки. Там же «Помощь» и частые вопросы.' },
];

/** Сквозные проверки (`tools/`) жмут кнопки сразу после входа; знакомство проверяют с флагом `skoka:coach`. */
function automated(): boolean {
  if (typeof navigator === 'undefined' || !navigator.webdriver) return false;
  try { return localStorage.getItem('skoka:coach') !== '1'; } catch { return true; }
}

/** Увиденные за этот запуск: хранилище перечитывается не сразу. */
const shown = new Set<string>();
/** Показать ли знакомство: не видели ни раньше, ни за этот запуск. */
export const tourWanted = (dismissed: readonly string[], hint: string = HOME_TOUR_HINT): boolean =>
  !dismissed.includes(hint) && !shown.has(hint) && !automated();

/**
 * Как заполнять операцию — по окну записи. Первый шаг заменяет прежнюю
 * подсказку «Листай пальцем»: два обучения подряд при первом открытии были бы
 * лишними, поэтому та считается увиденной вместе с этим.
 */
export const RECORD_STEPS: Step[] = [
  { target: '.opbar', title: 'Что записываешь',
    text: 'Расход, приход, перевод, в цель или долг — тапом или свайпом по листу.' },
  { target: '.opsum', title: 'Сумма',
    text: 'Набери на клавиатуре снизу. Можно считать: 350 + 120 — запишется итог.' },
  { target: '.opcards', title: 'Счёт',
    text: 'С какого счёта или карты. Другой — свайпом по карточке.' },
  { target: '.opcats', title: 'Категория',
    text: 'Куда ушли деньги: частые — плитками, остальные — «Все».' },
  { target: '.oprows.opv2 .opchips', title: 'Когда и часть плана',
    text: 'Дата — сегодня, если не поменять. Часть плана — нужды или свободные траты, обычно ставится по категории.' },
  { target: '.modesbox', title: 'Голосом, чек, файл',
    text: 'Скажи «кофе 300 с карты» или сфотографируй чек — поля заполнятся сами.' },
  { target: '.btn.pri[data-pad-enter]', title: 'Записать',
    text: 'Готово — операция в «Операциях», «Можно тратить» пересчитается сразу.' },
];

type Box = { top: number; left: number; width: number; height: number };

/** Видимые элементы шага: на компьютере плюса нет, у нового человека — «Проверь сегодня». */
function targets(s: Step): Element[] {
  return (Array.isArray(s.target) ? s.target : [s.target])
    .map((sel) => document.querySelector(sel))
    .filter((e): e is Element => e !== null && e.getBoundingClientRect().width > 0);
}

export const HomeTour = ({ onDone }: { onDone: () => void }) => <Tour steps={STEPS} hint={HOME_TOUR_HINT} onDone={onDone} />;

export function Tour({ steps: all, hint, also = [], onDone }: {
  steps: readonly Step[]; hint: string;
  /** Подсказки, которые этот обход заменяет: считаются увиденными вместе с ним. */
  also?: readonly string[];
  onDone: () => void;
}) {
  // Шаги — после того как экран лёг в документ: при первой отрисовке его ещё нет.
  const [steps, setSteps] = useState<Step[] | null>(null);
  useEffect(() => {
    const t = window.setTimeout(() => setSteps(all.filter((s) => targets(s).length > 0)), 600);
    return () => window.clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [at, setAt] = useState(0);
  const [box, setBox] = useState<Box | null>(null);
  const step = steps?.[at];

  const finish = () => {
    for (const h of [hint, ...also]) { shown.add(h); void dismissHint(h); }
    onDone();
  };

  useLayoutEffect(() => {
    if (!step) return;
    const els = targets(step);
    if (els.length === 0) return;
    els[0]!.scrollIntoView({ block: 'center', behavior: 'instant' as ScrollBehavior });
    const measure = () => {
      const rs = els.map((e) => e.getBoundingClientRect());
      const top = Math.min(...rs.map((r) => r.top)), left = Math.min(...rs.map((r) => r.left));
      const bottom = Math.max(...rs.map((r) => r.bottom)), right = Math.max(...rs.map((r) => r.right));
      setBox({ top: top - 6, left: left - 6, width: right - left + 12, height: bottom - top + 12 });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [step]);

  useEffect(() => { if (steps && steps.length === 0) finish(); }, [steps]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!steps || !step || !box) return null;

  const last = at === steps.length - 1;
  // Текст — там, где больше места: под подсвеченным или над ним.
  const below = box.top + box.height + 16;
  const roomBelow = window.innerHeight - below;
  const place = roomBelow > 200 || roomBelow > box.top
    ? { top: below } : { bottom: window.innerHeight - box.top + 16 };

  return createPortal(
    <div className="tour" role="dialog" aria-label={step.title}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
      <div className="tour-hole" style={box} aria-hidden="true" />
      <div className="tour-box" style={place}>
        <span className="tour-n">{at + 1} из {steps.length}</span>
        <b className="coach-title">{step.title}</b>
        <p className="coach-text">{step.text}</p>
        <div className="tour-acts">
          {!last && <button type="button" className="tour-skip" onClick={finish}>Пропустить</button>}
          <button type="button" className="coach-ok" onClick={() => (last ? finish() : setAt(at + 1))}>
            {last ? 'Понятно' : 'Дальше'}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
